import React, { useState, useEffect } from 'react';
import withRoleAccess from '../../hoc/withRoleAccess';
import {
  Card,
  Row,
  Col,
  DatePicker,
  Button,
  Statistic,
  Spin,
  Alert,
  Typography,
  Space,
  Table,
  Progress,
  message,
  Avatar,
  Badge
} from 'antd';
import {
  HomeOutlined,
  ClockCircleOutlined,
  ReloadOutlined,
  CalendarOutlined,
  FilePdfOutlined,
  TrophyOutlined,
  BarChartOutlined,
  FireOutlined,
  DashboardOutlined,
  RiseOutlined,
  StarOutlined
} from '@ant-design/icons';
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip as RechartsTooltip, ResponsiveContainer, Area, AreaChart } from 'recharts';
import dayjs from 'dayjs';

const { RangePicker } = DatePicker;
const { Title, Text } = Typography;

const API_URL = process.env.REACT_APP_API_URL;

const ClassroomReport = () => {
const [dateRange, setDateRange] = useState([
  dayjs().startOf('year'), 
  dayjs().endOf('year')    
]);

  
  const [utilizationData, setUtilizationData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  // Real API calls using fetch
  const fetchUtilizationData = async () => {
    const token = localStorage.getItem('token');
    const response = await fetch(`${API_URL}/report/class-reports/classroom-utilization?from=${dateRange[0].format('YYYY-MM-DD')}&to=${dateRange[1].format('YYYY-MM-DD')}`, {
      headers: { Authorization: `Bearer ${token}` }
    });
    if (!response.ok) throw new Error('Failed to fetch classroom utilization');
    return await response.json();
  };

  const loadData = async () => {
    if (!API_URL) {
      setError('API URL is not configured. Please set REACT_APP_API_URL in your environment variables.');
      return;
    }

    const token = localStorage.getItem('token');
    if (!token) {
      setError('Authentication token not found. Please log in again.');
      return;
    }

    setLoading(true);
    setError(null);
    
    try {
      const data = await fetchUtilizationData();
      setUtilizationData(data);
      message.success('Classroom utilization data loaded successfully');
    } catch (err) {
      console.error('Error loading classroom utilization data:', err);
      
      let errorMessage = 'Failed to load classroom utilization data';
      if (err.status) {
        if (err.status === 401) {
          errorMessage = 'Authentication failed. Please log in again.';
        } else if (err.status === 400) {
          errorMessage = 'Invalid request parameters';
        } else {
          errorMessage = `Server error: ${err.status}`;
        }
      } else {
        errorMessage = err.message || 'An unexpected error occurred';
      }
      
      setError(errorMessage);
      message.error(errorMessage);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (dateRange && dateRange.length === 2 && dateRange[0] && dateRange[1]) {
      loadData();
    }
  }, [dateRange]);

  const handleDateRangeChange = (dates) => {
    if (dates && dates.length === 2) {
      setDateRange(dates);
    }
  };

  const handleManualRefresh = () => {
    loadData();
  };

  const handlePrintPDF = () => {
    // Open a new window for printing
    const printWindow = window.open('', '_blank', 'width=800,height=600');
    
    // Get the content to print
    const reportContainer = document.getElementById('classroom-report-container');
    const printContent = reportContainer ? reportContainer.innerHTML : '';
    
    // Create the print document
    const printDocument = `
      <!DOCTYPE html>
      <html>
        <head>
          <title>Classroom Utilization Report</title>
          <link rel="stylesheet" href="https://cdnjs.cloudflare.com/ajax/libs/antd/5.0.0/reset.css">
          <style>
            body { 
              font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif;
              margin: 0;
              padding: 20px;
              background: white;
            }
            .ant-card { 
              box-shadow: none !important; 
              border: 1px solid #d9d9d9 !important;
              margin-bottom: 16px;
              page-break-inside: avoid;
            }
            .ant-statistic-title { font-weight: bold !important; }
            .ant-statistic-content { font-size: 18px !important; }
            .ant-card-head { 
              border-bottom: 2px solid #1890ff !important; 
              background: #f0f2f5 !important;
            }
            .ant-card-head-title { 
              font-weight: bold !important; 
              color: #1890ff !important; 
            }
            .print-header { 
              display: block !important;
              text-align: center; 
              margin-bottom: 20px; 
              border-bottom: 3px solid #1890ff; 
              padding-bottom: 10px; 
            }
            .print-date-range { 
              display: block !important;
              text-align: center; 
              margin-bottom: 15px; 
              font-weight: bold; 
            }
            svg { max-width: 100% !important; height: auto !important; }
            .no-print { display: none !important; }
            @page { 
              margin: 15mm; 
              size: A4; 
            }
          </style>
        </head>
        <body>
          ${printContent}
          <script>
            window.onload = function() {
              window.print();
              window.onafterprint = function() {
                window.close();
              };
            };
          </script>
        </body>
      </html>
    `;
    
    // Write the document and trigger print
    printWindow.document.write(printDocument);
    printWindow.document.close();
  };

  // Prepare chart data
  const roomsData = utilizationData?.rooms?.map(room => ({
    name: room.classroom_number ? `Room ${room.classroom_number}` : 'Unknown Room',
    classes: parseInt(room.total_classes) || 0,
    minutes: parseInt(room.total_minutes) || 0,
    hours: Math.round((parseInt(room.total_minutes) || 0) / 60 * 100) / 100
  })).sort((a, b) => b.classes - a.classes) || [];

  const peakTimesData = utilizationData?.peak_times?.map(time => ({
    hour: time.hour_slot,
    count: parseInt(time.count) || 0
  })) || [];

  // Calculate statistics
  const totalRooms = roomsData.length;
  const totalClasses = roomsData.reduce((sum, room) => sum + room.classes, 0);
  const totalHours = roomsData.reduce((sum, room) => sum + room.hours, 0);
  const averageClassesPerRoom = totalRooms > 0 ? Math.round(totalClasses / totalRooms * 100) / 100 : 0;
  const mostUsedRoom = utilizationData?.most_used;
  const peakTime = peakTimesData.length > 0 ? peakTimesData[0] : null;

  // Calculate utilization percentages (assuming 8 hours per day as full utilization)
  const maxPossibleHours = totalRooms * 8 * dayjs(dateRange[1]).diff(dayjs(dateRange[0]), 'day');
  const overallUtilization = maxPossibleHours > 0 ? Math.round((totalHours / maxPossibleHours) * 100 * 100) / 100 : 0;

  // Table columns for room utilization
  const roomColumns = [
    {
      title: 'Rank',
      dataIndex: 'rank',
      key: 'rank',
      width: 70,
      render: (_, __, index) => {
        if (index === 0) {
          return <TrophyOutlined style={{ color: '#faad14', fontSize: '18px' }} />;
        } else if (index === 1) {
          return <Badge count={2} style={{ backgroundColor: '#c0c0c0' }} />;
        } else if (index === 2) {
          return <Badge count={3} style={{ backgroundColor: '#cd7f32' }} />;
        }
        return index + 1;
      }
    },
    {
      title: 'Classroom',
      dataIndex: 'name',
      key: 'name',
      render: (name, record, index) => (
        <Space>
          <Avatar 
            icon={<HomeOutlined />} 
            style={{ 
              backgroundColor: index === 0 ? '#faad14' : index === 1 ? '#c0c0c0' : index === 2 ? '#cd7f32' : '#1890ff',
              border: index < 3 ? '2px solid currentColor' : 'none'
            }} 
          />
          <Text strong={index === 0}>
            {name}
            {index === 0 && <StarOutlined style={{ color: '#faad14', marginLeft: 8 }} />}
          </Text>
        </Space>
      )
    },
    {
      title: 'Total Classes',
      dataIndex: 'classes',
      key: 'classes',
      render: (classes, record, index) => (
        <Text strong={index === 0} style={{ color: index === 0 ? '#faad14' : undefined }}>
          {classes}
        </Text>
      )
    },
    {
      title: 'Total Hours',
      dataIndex: 'hours',
      key: 'hours',
      render: (hours) => `${hours} hrs`
    },
    {
      title: 'Utilization',
      key: 'utilization',
      render: (_, record) => {
        const daysDiff = dayjs(dateRange[1]).diff(dayjs(dateRange[0]), 'day') || 1;
        const maxHours = daysDiff * 8; // Assuming 8 hours per day as max
        const utilization = Math.round((record.hours / maxHours) * 100);
        return (
          <Progress
            percent={Math.min(utilization, 100)}
            size="small"
            strokeColor={
              utilization >= 80 ? '#52c41a' :
              utilization >= 60 ? '#faad14' :
              utilization >= 40 ? '#fa8c16' : '#f5222d'
            }
          />
        );
      }
    }
  ];

  const CustomTooltip = ({ active, payload, label }) => {
    if (active && payload && payload.length) {
      return (
        <div className="bg-white p-3 border border-gray-200 rounded-lg shadow-lg">
          <p className="font-semibold">{`${label}`}</p>
          {payload.map((entry, index) => (
            <p key={index} style={{ color: entry.color }}>
              {`${entry.dataKey}: ${entry.value}`}
            </p>
          ))}
        </div>
      );
    }
    return null;
  };

  // Generate utilization colors based on percentage
  const getUtilizationColor = (percentage) => {
    if (percentage >= 80) return '#52c41a';
    if (percentage >= 60) return '#faad14';
    if (percentage >= 40) return '#fa8c16';
    return '#f5222d';
  };

  return (
    <div style={{ padding: '24px', backgroundColor: '#f5f5f5', minHeight: '100vh' }}>
      <div id="classroom-report-container" style={{ maxWidth: '1400px', margin: '0 auto' }}>
        {/* Print Header - Only visible when printing */}
        <div className="print-header" style={{ display: 'none' }}>
          <Title level={2} style={{ margin: 0, color: '#1890ff' }}>
            Classroom Utilization Report
          </Title>
          <Text>Generated on {new Date().toLocaleDateString()}</Text>
        </div>

        {/* Print Date Range - Only visible when printing */}
        <div className="print-date-range" style={{ display: 'none' }}>
          Report Period: {dateRange[0]?.format('YYYY-MM-DD')} to {dateRange[1]?.format('YYYY-MM-DD')}
        </div>

        {/* Header */}
        <div style={{ marginBottom: '24px' }}>
          <Title level={2} style={{ marginBottom: '8px' }}>
            Classroom Utilization Report
          </Title>
          <Text type="secondary">
            Comprehensive analysis of classroom usage, peak times, and space optimization
          </Text>
        </div>

        {/* Date Range and Controls */}
        <Card style={{ marginBottom: '24px' }}>
          <Space size="large" wrap>
            <Space>
              <CalendarOutlined />
              <Text strong>Date Range:</Text>
              <RangePicker
                value={dateRange as any}
                onChange={handleDateRangeChange}
                format="YYYY-MM-DD"
                allowClear={false}
              />
            </Space>
            <Space>
              <Button 
                type="primary" 
                icon={<ReloadOutlined />}
                onClick={handleManualRefresh}
                loading={loading}
              >
                Update Report
              </Button>
              <Button 
                type="default" 
                icon={<FilePdfOutlined />}
                onClick={handlePrintPDF}
                disabled={!utilizationData}
              >
                Print to PDF
              </Button>
            </Space>
          </Space>
        </Card>

        {error && (
          <Alert
            message="Error"
            description={error}
            type="error"
            showIcon
            style={{ marginBottom: '24px' }}
          />
        )}

        <Spin spinning={loading}>
          {/* Key Metrics */}
          <Row gutter={[16, 16]} style={{ marginBottom: '24px' }}>
            <Col xs={24} sm={12} lg={6}>
              <Card>
                <Statistic
                  title="Total Classrooms"
                  value={totalRooms}
                  prefix={<HomeOutlined />}
                  valueStyle={{ color: '#1890ff' }}
                />
              </Card>
            </Col>
            <Col xs={24} sm={12} lg={6}>
              <Card>
                <Statistic
                  title="Total Classes"
                  value={totalClasses}
                  prefix={<BarChartOutlined />}
                  valueStyle={{ color: '#52c41a' }}
                />
              </Card>
            </Col>
            <Col xs={24} sm={12} lg={6}>
              <Card>
                <Statistic
                  title="Avg Classes/Room"
                  value={averageClassesPerRoom}
                  precision={1}
                  prefix={<DashboardOutlined />}
                  valueStyle={{ color: '#722ed1' }}
                />
              </Card>
            </Col>
            <Col xs={24} sm={12} lg={6}>
              <Card>
                <Statistic
                  title="Overall Utilization"
                  value={overallUtilization}
                  precision={1}
                  suffix="%"
                  prefix={<RiseOutlined />}
                  valueStyle={{ 
                    color: getUtilizationColor(overallUtilization)
                  }}
                />
              </Card>
            </Col>
          </Row>

          {/* Highlight Cards */}
          <Row gutter={[16, 16]} style={{ marginBottom: '24px' }}>
            {/* Most Used Room */}
            <Col xs={24} lg={12}>
              <Card 
                title={
                  <Space>
                    <TrophyOutlined style={{ color: '#faad14' }} />
                    Most Used Classroom
                  </Space>
                }
                style={{ background: 'linear-gradient(135deg, #fff7e6 0%, #fff2e6 100%)' }}
              >
                {mostUsedRoom ? (
                  <div style={{ textAlign: 'center', padding: '20px 0' }}>
                    <Avatar 
                      size={64} 
                      icon={<HomeOutlined />} 
                      style={{ backgroundColor: '#faad14', marginBottom: '16px' }}
                    />
                    <Title level={3} style={{ margin: '8px 0', color: '#faad14' }}>
                      Room {mostUsedRoom.classroom_number || 'Unknown'}
                    </Title>
                    <Text style={{ fontSize: '18px' }}>
                      <strong>{mostUsedRoom.class_count}</strong> classes taught
                    </Text>
                  </div>
                ) : (
                  <div style={{ textAlign: 'center', padding: '40px 0' }}>
                    <Text type="secondary">No data available</Text>
                  </div>
                )}
              </Card>
            </Col>

            {/* Peak Time */}
            <Col xs={24} lg={12}>
              <Card 
                title={
                  <Space>
                    <FireOutlined style={{ color: '#f5222d' }} />
                    Peak Usage Time
                  </Space>
                }
                style={{ background: 'linear-gradient(135deg, #fff1f0 0%, #ffe7e6 100%)' }}
              >
                {peakTime ? (
                  <div style={{ textAlign: 'center', padding: '20px 0' }}>
                    <Avatar 
                      size={64} 
                      icon={<ClockCircleOutlined />} 
                      style={{ backgroundColor: '#f5222d', marginBottom: '16px' }}
                    />
                    <Title level={3} style={{ margin: '8px 0', color: '#f5222d' }}>
                      {peakTime.hour}
                    </Title>
                    <Text style={{ fontSize: '18px' }}>
                      <strong>{peakTime.count}</strong> classes scheduled
                    </Text>
                  </div>
                ) : (
                  <div style={{ textAlign: 'center', padding: '40px 0' }}>
                    <Text type="secondary">No data available</Text>
                  </div>
                )}
              </Card>
            </Col>
          </Row>

          {/* Charts Row */}
          <Row gutter={[16, 16]} style={{ marginBottom: '24px' }}>
            {/* Classroom Usage Chart */}
            <Col xs={24} lg={12}>
              <Card 
                title={
                  <Space>
                    <HomeOutlined style={{ color: '#1890ff' }} />
                    Classroom Usage Distribution
                  </Space>
                }
              >
                {roomsData.length > 0 ? (
                  <ResponsiveContainer width="100%" height={300}>
                    <BarChart data={roomsData.slice(0, 10)} margin={{ top: 20, right: 30, left: 20, bottom: 5 }}>
                      <CartesianGrid strokeDasharray="3 3" />
                      <XAxis dataKey="name" angle={-45} textAnchor="end" height={100} />
                      <YAxis />
                      <RechartsTooltip content={<CustomTooltip active={undefined} payload={undefined} label={undefined} />} />
                      <Bar dataKey="classes" fill="#1890ff" radius={[4, 4, 0, 0]} />
                    </BarChart>
                  </ResponsiveContainer>
                ) : (
                  <div style={{ textAlign: 'center', padding: '60px 0' }}>
                    <Text type="secondary">No classroom data available</Text>
                  </div>
                )}
              </Card>
            </Col>

            {/* Peak Times Chart */}
            <Col xs={24} lg={12}>
              <Card 
                title={
                  <Space>
                    <ClockCircleOutlined style={{ color: '#52c41a' }} />
                    Peak Usage Hours
                  </Space>
                }
              >
                {peakTimesData.length > 0 ? (
                  <ResponsiveContainer width="100%" height={300}>
                    <AreaChart data={peakTimesData} margin={{ top: 20, right: 30, left: 20, bottom: 5 }}>
                      <CartesianGrid strokeDasharray="3 3" />
                      <XAxis dataKey="hour" />
                      <YAxis />
                      <RechartsTooltip content={<CustomTooltip active={undefined} payload={undefined} label={undefined} />} />
                      <Area
                        type="monotone" 
                        dataKey="count" 
                        stroke="#52c41a" 
                        fill="#52c41a" 
                        fillOpacity={0.3}
                      />
                    </AreaChart>
                  </ResponsiveContainer>
                ) : (
                  <div style={{ textAlign: 'center', padding: '60px 0' }}>
                    <Text type="secondary">No peak time data available</Text>
                  </div>
                )}
              </Card>
            </Col>
          </Row>

          {/* Detailed Utilization Table */}
          <Card 
            title={
              <Space>
                <DashboardOutlined style={{ color: '#722ed1' }} />
                Detailed Classroom Utilization
              </Space>
            }
          >
            {roomsData.length > 0 ? (
              <Table
                dataSource={roomsData}
                columns={roomColumns}
                rowKey={(record, index) => index}
                pagination={{ pageSize: 10 }}
                scroll={{ x: 'max-content' }}
              />
            ) : (
              <div style={{ textAlign: 'center', padding: '40px 0' }}>
                <Text type="secondary">No classroom utilization data available</Text>
              </div>
            )}
          </Card>

          {/* Utilization Insights */}
          {roomsData.length > 0 && (
            <Card title="Utilization Insights" style={{ marginTop: '24px' }}>
              <Row gutter={[16, 16]}>
                <Col xs={24} sm={8}>
                  <div style={{ textAlign: 'center', padding: '20px' }}>
                    <div style={{ 
                      width: '60px', 
                      height: '60px', 
                      borderRadius: '50%', 
                      backgroundColor: '#52c41a', 
                      display: 'flex', 
                      alignItems: 'center', 
                      justifyContent: 'center', 
                      margin: '0 auto 16px',
                      color: 'white',
                      fontSize: '24px',
                      fontWeight: 'bold'
                    }}>
                      {roomsData.filter(room => {
                        const daysDiff = dayjs(dateRange[1]).diff(dayjs(dateRange[0]), 'day') || 1;
                        const maxHours = daysDiff * 8;
                        return (room.hours / maxHours) * 100 >= 80;
                      }).length}
                    </div>
                    <Text strong>High Utilization</Text>
                    <br />
                    <Text type="secondary">≥ 80% usage</Text>
                  </div>
                </Col>
                <Col xs={24} sm={8}>
                  <div style={{ textAlign: 'center', padding: '20px' }}>
                    <div style={{ 
                      width: '60px', 
                      height: '60px', 
                      borderRadius: '50%', 
                      backgroundColor: '#faad14', 
                      display: 'flex', 
                      alignItems: 'center', 
                      justifyContent: 'center', 
                      margin: '0 auto 16px',
                      color: 'white',
                      fontSize: '24px',
                      fontWeight: 'bold'
                    }}>
                      {roomsData.filter(room => {
                        const daysDiff = dayjs(dateRange[1]).diff(dayjs(dateRange[0]), 'day') || 1;
                        const maxHours = daysDiff * 8;
                        const utilization = (room.hours / maxHours) * 100;
                        return utilization >= 40 && utilization < 80;
                      }).length}
                    </div>
                    <Text strong>Medium Utilization</Text>
                    <br />
                    <Text type="secondary">40-79% usage</Text>
                  </div>
                </Col>
                <Col xs={24} sm={8}>
                  <div style={{ textAlign: 'center', padding: '20px' }}>
                    <div style={{ 
                      width: '60px', 
                      height: '60px', 
                      borderRadius: '50%', 
                      backgroundColor: '#f5222d', 
                      display: 'flex', 
                      alignItems: 'center', 
                      justifyContent: 'center', 
                      margin: '0 auto 16px',
                      color: 'white',
                      fontSize: '24px',
                      fontWeight: 'bold'
                    }}>
                      {roomsData.filter(room => {
                        const daysDiff = dayjs(dateRange[1]).diff(dayjs(dateRange[0]), 'day') || 1;
                        const maxHours = daysDiff * 8;
                        return (room.hours / maxHours) * 100 < 40;
                      }).length}
                    </div>
                    <Text strong>Low Utilization</Text>
                    <br />
                    <Text type="secondary">40% usage</Text>
                  </div>
                </Col>
              </Row>
            </Card>
          )}
        </Spin>

        <style {...{jsx: "true"} as any}>{`
          .print-header, .print-date-range {
            display: none;
          }
          
          @media print {
            .print-header, .print-date-range {
              display: block !important;
            }
          }
        `}</style>
      </div>
    </div>
  );
};

export default withRoleAccess(ClassroomReport, ['superadmin']);